import { Network } from '@manifoldxyz/js-ts-utils';
import { CLAIM_FEE_ADDRESS, CLAIM_FEE_PER_MINT } from '@/common/constants';
import { APIHelpers } from './apiHelpers';

// @TODO: bad any types in here
type ReservoirStep = {
  error?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorData?: any;
  action: string;
  description: string;
  kind: 'transaction' | 'signature';
  id: string;
  items?: {
    status: 'complete' | 'incomplete';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
    orderIndexes?: number[];
    orderIds?: string[];
    // Manually added
    error?: string;
    txHash?: string;
    orderData?: {
      crossPostingOrderId?: string;
      orderId: string;
      orderIndex: string;
    }[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    transfersData?: any['/transfers/bulk/v1']['get']['responses']['200']['schema']['transfers'];
  }[];
};

// @see @reservoir0x/reservoir-sdk
type Execute = {
  requestId?: string;
  errors?: { message?: string; orderId?: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  preview?: any;
  path: string;
  error?: string; // Manually added client error
  steps: ReservoirStep[];
};

export type FloorAsk = {
  maker: string;
  price: {
    amount: {
      decimal: number;
      raw: string;
      usd: number;
      native: number;
    };
    currency: {
      contract: string;
      decimals: number;
      name: string;
      symbol: string;
    };
  };
};

export type TokenResponse = {
  token: {
    tokenId: string;
    name: string;
    kind: string;
    description: string;
    contract: string;
    owner: string;
    chainId: number;
  };
  market: {
    floorAsk: FloorAsk;
  };
};

interface ReservoirTransactionData {
  from: string;
  to: string;
  data: string;
  value: string;
}

class ReservoirAPI extends APIHelpers {
  constructor() {
    super();
    // @dev is changed dynamically before every endpoint call using getBaseReservoirURL
    this.baseUrl = 'https://api.reservoir.tools';
  }

  private getBaseReservoirURL(chainId: number): string {
    switch (chainId) {
      case 1:
        return 'https://api.reservoir.tools';
      default: {
        const network = Network.getDisplayNameForNetworkId(chainId);
        return `https://api-${network}.reservoir.tools`;
      }
    }
  }

  /**
   * @dev get floor prices for a collection of tokens
   * @param contractAddress
   * @param chainId
   * @returns
   */
  async getFloorPrices(
    contractAddress: string,
    chainId: number,
  ): Promise<{ [key: string]: number } | undefined> {
    try {
      this.baseUrl = this.getBaseReservoirURL(chainId);
      const response = await this.fetchWithTimeout(`/tokens/floor/v1`, undefined, {
        collection: contractAddress,
      });
      return response.tokens;
    } catch (e) {
      console.error('Error getting floor prices from reservoir', e);
      return undefined;
    }
  }

  /**
   * Used to get all tokens for a collection and then sift through them to find the ones
   * that match the name of the token in the claim. Then we can use that token's floor
   * ask to get the price of the token for secondary purchasing.
   * @param contractAddress creatorContractAddress
   * @param tokenIds array of tokens (will return in same order)
   * @param chainId chainId
   * @returns array of tokens with metadata
   */
  async getTokensMetadata(
    contractAddress: string,
    tokenIds: string[],
    chainId: number,
  ): Promise<TokenResponse[]> {
    if (!tokenIds.length) {
      return [];
    }

    try {
      this.baseUrl = this.getBaseReservoirURL(chainId);
      const tokens = tokenIds
        .map((tokenId) => `${contractAddress}:${tokenId}&tokens=`)
        .join('')
        .slice(0, -8);
      const response = await this.fetchWithTimeout(`/tokens/v6?tokens=${tokens}`);

      if (Array.isArray(response.tokens)) {
        return response.tokens;
      }
      return [];
    } catch (e) {
      console.error('Error getting tokens metadata from reservoir', e);
      return [];
    }
  }

  async getBuyTransactionData(
    contractAddress: string,
    chainId: number,
    tokenId: string,
    taker: string,
    amount: number, // amounts over 1 are untested
  ): Promise<ReservoirTransactionData | undefined> {
    this.baseUrl = this.getBaseReservoirURL(chainId);
    const tokenOrCollection =
      amount > 1 ? { collection: contractAddress } : { token: `${contractAddress}:${tokenId}` };

    try {
      const response = await this.post('/execute/buy/v7', {
        body: JSON.stringify({
          items: [
            {
              quantity: amount,
              ...tokenOrCollection,
            },
          ],
          onlyPath: false,
          normalizeRoyalties: true,
          allowInactiveOrderIds: false,
          feesOnTop: [`${CLAIM_FEE_ADDRESS}:${CLAIM_FEE_PER_MINT.mul(amount)}`],
          partial: false,
          skipBalanceCheck: false,
          excludeEOA: false,
          source: 'manifold.xyz',
          taker,
        }),
      });

      if (response.errors) {
        // switch (response.data.message) {
        //   case 'Unable to fill requested quantity':
        //     throw new Error('Unable to fill requested quantity');
        //   case 'No fillable orders (taker cannot fill own orders)':
        //     throw new Error('Cannot fill own order');
        //   default:
        //     throw new Error('Non-200 response from reservoir');
        // }
      }

      const { steps } = response as Execute;

      if (steps.length) {
        const signatures = steps.filter(
          (step: ReservoirStep) =>
            step.kind === 'signature' && step.items && step?.items.length > 0,
        );
        if (signatures.length > 0 && signatures[0].items) {
          return signatures[0].items[0]?.data;
        }

        const sales = steps.filter(
          (step: ReservoirStep) => step?.id === 'sale' && step?.items?.[0]?.status === 'incomplete',
        );

        if (sales.length > 0) {
          return sales[0].items && sales[0].items[0].data;
        }
      }
      console.error('Invalid step processing error with reservoir', response);
      throw new Error('Invalid step processing error');
    } catch (e) {
      console.error('Error getting buy transaction data from reservoir', e);
      throw new Error('Error getting buy transaction data from reservoir: ' + e);
    }
  }
}

// Export singleton instance
export const reservoirAPI = new ReservoirAPI();
